<template>
    <common-modal width="medium" :showAsInnerModal="true" @close="$emit('close')">
        <common-content-wrapper width="medium">
            <common-content-spacer height="medium" class="upload-additional-records" :isCenter="false">
                <common-page-header
                    :heading="$t('pages.referrals.new.dropdown.label.referring.upload_requested_records')"
                    size="large"
                ></common-page-header>
                <div class="referral-tracking-modal__section">
                    <div class="card-provider">
                        <div class="provider-details">
                            <div class="provider">
                                <div class="user-avatar">
                                    <common-user-avatar size="" :user="getAvatarUser(provider)"></common-user-avatar>
                                </div>

                                <div>
                                    <p class="name">{{ providerName }}</p>
                                    <p class="sub">{{ providerSpeciality }} • {{ practiceName }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="allRequests.length" class="upload-additional-records__stats-bar">
                    <span class="stats-label">
                        {{ $t('modals.upload_records_modal.title') }}
                        <strong>({{ allRequests.length }})</strong>
                    </span>

                    <div class="stats-badges">
                        <span v-if="pendingRequestsCount > 0" class="badge badge--pending">
                            <span class="badge-dot"></span>
                            {{ pendingRequestsCount }} {{ $t('modals.upload_records_modal.pending') }}
                        </span>

                        <span v-if="fulfilledRequestsCount > 0" class="badge badge--fulfilled">
                            <span class="badge-icon">✓</span>
                            {{ fulfilledRequestsCount }} {{ $t('modals.upload_records_modal.fulfilled') }}
                        </span>
                    </div>
                </div>

                <common-loader v-if="isFetchingDocuments" :isProcessing="isFetchingDocuments"></common-loader>

                <div v-else-if="allRequests.length">
                    <common-accordion
                        v-for="(req, index) in allRequests"
                        :key="req.id || index"
                        :defaultOpen="false"
                        class="request-card"
                        :class="requestCardClass(req)"
                    >
                        <template #header>
                            <div class="request-card__header">
                                <div class="request-card__header-title">
                                    <span class="request-number">
                                        {{ $t('modals.upload_records_modal.requests', { n: index + 1 }) }}
                                    </span>
                                    <span v-if="req.recordTypeIds && req.recordTypeIds.length" class="doc-count-tag">
                                        {{
                                            $t('modals.upload_records_modal.documents_requested', {
                                                n: req.recordTypeIds.length,
                                                label:
                                                    req.recordTypeIds.length === 1
                                                        ? $t('modals.upload_records_modal.doc')
                                                        : $t('modals.upload_records_modal.docs'),
                                            })
                                        }}
                                    </span>
                                </div>

                                <div class="request-card__header-status">
                                    <span class="status-pill" :class="statusPillClass(req)">
                                        <span class="status-dot"></span>
                                        {{ formatStatus(req.status) }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <div v-if="req.message" class="request-card__message-box">
                            <div class="message-header">
                                <common-icon name="message" size="semi-small"></common-icon>
                                <span class="message-label">{{ $t('modals.upload_records_modal.note_title') }}</span>
                            </div>
                            <p class="message-text">{{ req.message }}</p>
                        </div>

                        <div v-if="req.recordTypeIds && req.recordTypeIds.length" class="request-card__docs-requested">
                            <label class="field-label">{{ $t('modals.upload_records_modal.requested_docs') }}</label>

                            <div class="doc-tags-container">
                                <span v-for="docId in req.recordTypeIds" :key="docId" class="doc-tag">
                                    <common-icon name="file" size="semi-small"></common-icon>
                                    {{ getDocName(docId) }}
                                </span>
                            </div>
                        </div>

                        <input
                            :ref="'fileInput_' + (req.id || index)"
                            type="file"
                            multiple
                            style="display: none"
                            accept="application/pdf,image/png,image/jpg,image/jpeg"
                            @change="fileSelected($event, req.id || index)"
                        />

                        <div class="request-card__field">
                            <label class="field-label">{{ $t('modals.upload_records_modal.upload.title') }}</label>

                            <div
                                v-if="!getRequestState(req.id || index).selectedFiles.length"
                                class="request-card__dropzone"
                                :class="dropzoneClass(req.id || index)"
                                @dragover.prevent="setDragging(req.id || index, true)"
                                @dragleave.prevent="setDragging(req.id || index, false)"
                                @drop.prevent="handleFileDrop($event, req.id || index)"
                                @click="triggerFileInput(req.id || index)"
                            >
                                <common-icon name="cloud" size="gigantic"></common-icon>

                                <p class="dropzone-text">
                                    {{ $t('modals.upload_records_modal.upload.drag') }}
                                    <span class="browse-link">{{ $t('modals.upload_records_modal.upload.browse') }}</span>
                                </p>

                                <p class="dropzone-limits">
                                    {{ $t('pages.referrals.notes.add.file_limits') }}
                                </p>
                            </div>

                            <common-upload-summary
                                v-else
                                :isUploading="getRequestState(req.id || index).isUploading"
                                :files="getRequestState(req.id || index).selectedFiles"
                                @remove-file="removeFileFromRequest(req.id || index, $event)"
                            ></common-upload-summary>

                            <common-warning
                                v-if="getRequestState(req.id || index).showWarning"
                                size="medium"
                                :text="getRequestState(req.id || index).warningText"
                            ></common-warning>
                        </div>

                        <div class="request-card__field">
                            <label class="field-label">{{ $t('modals.upload_records_modal.upload.optional_note') }}</label>

                            <textarea
                                v-model="getRequestState(req.id || index).note"
                                class="note-input"
                                rows="2"
                                :placeholder="$t('modals.upload_records_modal.upload.placeholder')"
                            ></textarea>
                        </div>

                        <div class="request-card__actions">
                            <common-button
                                v-if="getRequestState(req.id || index).selectedFiles.length"
                                :label="$t('pages.referrals.notes.add.buttons.select_more')"
                                type="secondary"
                                size="small"
                                :disabled="getRequestState(req.id || index).isUploading"
                                @click="triggerFileInput(req.id || index)"
                            ></common-button>

                            <common-button
                                :label="$t('modals.patient_record.upload')"
                                type="primary"
                                size="small"
                                :disabled="isUploadDisabled(req.id || index)"
                                :isProcessing="getRequestState(req.id || index).isUploading"
                                @click="uploadFilesForRequest(req)"
                            ></common-button>
                        </div>
                    </common-accordion>
                </div>
            </common-content-spacer>
        </common-content-wrapper>
    </common-modal>
</template>

<script>
    import UploadMixin from 'mixins/UploadMixin'
    import PatientAPI from 'api/PatientAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsAPI from 'api/ReferralsAPI'

    export default {
        mixins: [UploadMixin],

        props: {
            isOpen: { type: Boolean, required: true },
            provider: { type: Object, required: true },
            referralPoolId: { type: [Number, String], required: false, default: null },
        },

        data() {
            return {
                documentOptions: [],
                isFetchingDocuments: false,
                requestStates: {},
            }
        },

        computed: {
            requestCardClass() {
                return (req) => ({
                    'request-card--pending': req.status === 'PENDING',
                    'request-card--fulfilled': req.status === 'FULFILLED',
                })
            },
            statusPillClass() {
                return (req) => ({
                    'status-pill--fulfilled': req.status === 'FULFILLED',
                    'status-pill--pending': req.status !== 'FULFILLED',
                })
            },
            dropzoneClass() {
                return (requestId) => ({
                    'is-dragging': this.getRequestState(requestId).isDragging,
                })
            },
            isUploadDisabled() {
                return (requestId) => {
                    const state = this.getRequestState(requestId)

                    return !state.selectedFiles.length || state.isSubmitDisabled || state.isUploading
                }
            },
            providerName() {
                if (!this.provider) return ''
                if (this.provider.type === 'external') {
                    const contact = this.provider.data?.contactInfo || {}
                    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
                    return contact.designation ? `${name}, ${contact.designation}` : name
                }
                const name =
                    [this.provider.firstName, this.provider.lastName].filter(Boolean).join(' ') || this.provider.name || ''
                return this.provider.designation ? `${name}, ${this.provider.designation}` : name
            },

            providerSpeciality() {
                if (!this.provider) return ''
                if (this.provider.type === 'external') {
                    return this.provider?.speciality || ''
                }
                return this.provider.specialities?.[0]?.speciality?.name || ''
            },

            practiceName() {
                return this.provider?.practice?.name || ''
            },

            allRequests() {
                return this.provider?.additionalRecordsRequest || []
            },

            pendingRequestsCount() {
                return this.allRequests.filter((r) => r.status === 'PENDING').length
            },

            fulfilledRequestsCount() {
                return this.allRequests.filter((r) => r.status === 'FULFILLED').length
            },
        },

        methods: {
            formatStatus(status) {
                return (status || 'PENDING')
                    .toLowerCase()
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            },

            getAvatarUser(provider) {
                if (!provider) return {}
                if (provider.type === 'external') {
                    return {
                        firstName: provider.data?.contactInfo?.firstName,
                        lastName: provider.data?.contactInfo?.lastName,
                        avatar: null,
                    }
                }
                return provider
            },

            initRequestStates() {
                const reqs = this.allRequests
                const states = { ...this.requestStates }

                reqs.forEach((req, idx) => {
                    const reqId = req.id || idx
                    if (!states[reqId]) {
                        this.$set(states, reqId, {
                            selectedFiles: [],
                            note: '',
                            isDragging: false,
                            isUploading: false,
                            warningText: '',
                            showWarning: false,
                            isSubmitDisabled: false,
                        })
                    }
                })

                this.requestStates = states
            },

            getRequestState(reqId) {
                if (!this.requestStates[reqId]) {
                    this.$set(this.requestStates, reqId, {
                        selectedFiles: [],
                        note: '',
                        isDragging: false,
                        isUploading: false,
                        warningText: '',
                        showWarning: false,
                        isSubmitDisabled: false,
                    })
                }
                return this.requestStates[reqId]
            },

            getDocumentType() {
                if (this.documentOptions.length || this.isFetchingDocuments) return
                this.isFetchingDocuments = true
                PatientAPI.getDocumentType()
                    .then((res) => {
                        this.documentOptions = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []
                    })
                    .catch(() => {
                        this.documentOptions = []
                    })
                    .finally(() => {
                        this.isFetchingDocuments = false
                    })
            },

            getDocName(docId) {
                const found = this.documentOptions.find((opt) => opt.id === docId || opt.value === docId)
                return found ? found.name || found.label : `Document #${docId}`
            },

            setDragging(reqId, val) {
                const state = this.getRequestState(reqId)
                state.isDragging = val
            },

            triggerFileInput(reqId) {
                const refName = `fileInput_${reqId}`
                const refEl = this.$refs[refName]
                if (refEl) {
                    const el = Array.isArray(refEl) ? refEl[0] : refEl
                    if (el) el.click()
                }
            },

            fileSelected(event, reqId) {
                const files = Array.from(event.target.files || [])
                if (!files.length) return
                this.addFilesToRequest(reqId, files)
                if (event.target) event.target.value = ''
            },

            handleFileDrop(event, reqId) {
                this.setDragging(reqId, false)
                const files = Array.from(event.dataTransfer?.files || [])
                if (files.length) {
                    this.addFilesToRequest(reqId, files)
                }
            },

            addFilesToRequest(reqId, files) {
                const state = this.getRequestState(reqId)
                files.forEach((file) => {
                    state.selectedFiles.push({
                        file: file,
                        fileSize: file.size,
                        progress: 0,
                        isUploaded: false,
                    })
                })
                this.checkForFileSizes(reqId)
            },

            removeFileFromRequest(reqId, fileObj) {
                const state = this.getRequestState(reqId)
                if (!state || state.isUploading) return
                state.selectedFiles = state.selectedFiles.filter((f) => f !== fileObj)
                this.checkForFileSizes(reqId)
            },

            checkForFileSizes(reqId) {
                const state = this.getRequestState(reqId)
                if (!state) return
                const UploadsAPI = require('api/UploadsAPI').default
                const limit = UploadsAPI.UPLOAD_MAX_SIZE
                const oversize = state.selectedFiles.find((f) => (f.fileSize || f.file?.size || 0) > limit)
                if (oversize) {
                    state.warningText =
                        this.$t('pages.referrals.notes.add.warnings.size_limit') ||
                        'A selected file exceeds the maximum allowed size.'
                    state.showWarning = true
                    state.isSubmitDisabled = true
                } else {
                    state.showWarning = false
                    state.isSubmitDisabled = false
                }
            },

            uploadFilesForRequest(req) {
                const reqId = req.id || 0
                const state = this.getRequestState(reqId)
                if (!state.selectedFiles.length || state.isUploading || state.isSubmitDisabled) return

                state.isUploading = true
                state.showWarning = false

                const UploadsAPI = require('api/UploadsAPI').default
                const nativeFiles = state.selectedFiles.map((f) => f.file)

                UploadsAPI.uploadFiles(nativeFiles, 'patient-documents')
                    .then((results) => {
                        const activeUserId = this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id

                        const payload = {
                            date: new Date().toISOString().split('T')[0],
                            files: results,
                            note: state.note || undefined,
                            recordTypeIds: req.recordTypeIds || [],
                            userId: activeUserId,
                        }

                        let uploadPromise
                        if (req.id) {
                            uploadPromise = ReferralsPoolAPI.uploadAdditionalRecordRequest(req.id, payload)
                        } else if (this.referralPoolId) {
                            uploadPromise = ReferralsPoolAPI.uploadAdditionalRecords(this.referralPoolId, payload)
                        } else {
                            const filesForUpload = state.selectedFiles.map((f, i) => ({
                                ...f,
                                uploadKey: results[i].uploadKey,
                            }))
                            uploadPromise = ReferralsAPI.uploadConsultationNotes(filesForUpload, this.provider.id)
                        }

                        return uploadPromise
                    })
                    .then(() => {
                        state.isUploading = false
                        req.status = 'FULFILLED'
                        state.selectedFiles = []
                        state.note = ''
                        this.$emit('records-uploaded', { requestId: req.id })
                    })
                    .catch((err) => {
                        state.isUploading = false
                        const parsed = UploadsAPI.parseUploadError(err)
                        if (parsed.code === 'upload.mime_type' || parsed.code === 'fileBase64.mimeType') {
                            state.warningText = this.$t('pages.referrals.notes.add.warnings.mime_type')
                        } else {
                            state.warningText = parsed.message || this.$t('common.upload.unsupported') || 'Error uploading files'
                        }
                        state.showWarning = true
                    })
            },
        },

        watch: {
            provider: {
                immediate: true,
                deep: true,
                handler() {
                    this.initRequestStates()
                },
            },
        },

        mounted() {
            this.getDocumentType()
            this.initRequestStates()
        },
    }
</script>

<style scoped lang="scss">
    .upload-additional-records {
        &__provider-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            background-color: var(--color-bg-secondary, #f8fafc);
            border-radius: 8px;
            border: 1px solid var(--color-border, #e2e8f0);
            margin-bottom: 16px;
        }

        &__provider-details {
            .provider-name {
                font-size: 1rem;
                font-weight: 600;
                color: var(--color-text-primary, #0f172a);
                margin: 0;
            }

            .provider-sub {
                font-size: 0.875rem;
                color: var(--color-text-secondary, #64748b);
                margin: 2px 0 0 0;
            }
        }

        &__stats-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            margin-bottom: 18px;
            padding: 12px 16px;

            background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            box-shadow: 0 2px 10px rgba(15, 23, 42, 0.05);

            .stats-label {
                display: flex;
                align-items: center;
                gap: 8px;

                font-size: 14px;
                font-weight: 600;
                color: #1f2937;
                white-space: nowrap;

                strong {
                    color: #2563eb;
                    font-weight: 700;
                }
            }

            .stats-badges {
                display: flex;
                align-items: center;
                gap: 10px;
                flex-wrap: wrap;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 6px;

                padding: 6px 12px;
                border-radius: 12px;

                font-size: 12px;
                font-weight: 600;
                line-height: 1;

                border: 1px solid;

                .badge-dot {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    flex-shrink: 0;
                }

                .badge-icon {
                    font-size: 13px;
                    font-weight: 700;
                    line-height: 1;
                }

                &--pending {
                    background: #fff7ed;
                    color: #ca3500;
                    border-color: #ffd6a7;

                    .badge-dot {
                        background: #ca3500;
                    }
                }

                &--fulfilled {
                    background: #f0fdf4;
                    color: #008236;
                    border-color: #b9f8cf;

                    .badge-icon {
                        color: #008236;
                    }
                }
            }
        }

        &__requests-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 20px;
            max-height: 480px;
            overflow-y: auto;
            padding-right: 4px;
        }

        .request-card {
            overflow: hidden;

            &:hover {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            }

            &__header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                width: 100%;
                gap: 16px;
            }

            &__header-title {
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1;
                min-width: 0;
            }

            &__header-status {
                flex-shrink: 0;

                .status-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.03em;

                    .status-dot {
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                    }

                    &--pending {
                        background: #fffbeb;
                        color: #b45309;
                        border: 1px solid #fde68a;

                        .status-dot {
                            background: #f59e0b;
                        }
                    }

                    &--fulfilled {
                        background: #ecfdf5;
                        color: #047857;
                        border: 1px solid #a7f3d0;

                        .status-dot {
                            background: #10b981;
                        }
                    }
                }
            }

            .doc-count-tag {
                display: inline-flex;
                align-items: center;
                padding: 2px 8px;
                border-radius: 10px;
                background: #e2e8f0;
                color: #64748b;
                font-size: 0.75rem;
                font-weight: 500;
            }

            &__message-box {
                margin-bottom: 16px;
                padding: 12px 14px;
                background: #eff6ff;
                border-left: 4px solid #3b82f6;
                border-radius: 6px;

                .message-header {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 6px;
                }

                .message-icon {
                    font-size: 0.9rem;
                }

                .message-label {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #1e40af;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                }

                .message-text {
                    margin: 0;
                    color: #1e3a8a;
                    font-size: 0.9rem;
                    line-height: 1.45;
                }
            }

            &__docs-requested {
                margin-bottom: 16px;
            }

            .field-label {
                display: block;
                margin-bottom: 6px;
                font-size: 0.85rem;
                font-weight: 600;
                color: #475569;
            }

            .doc-tags-container {
                display: flex;
                flex-wrap: wrap;
                gap: 6px;
            }

            .doc-tag {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 4px 10px;
                border: 1px solid #cbd5e1;
                border-radius: 6px;
                background: #f1f5f9;
                color: #334155;
                font-size: 0.8rem;
                font-weight: 500;
            }

            &__field {
                margin-bottom: 16px;
            }

            .note-input {
                width: 100%;
                padding: 8px 12px;
                border: 1px solid #cbd5e1;
                border-radius: 6px;
                resize: vertical;
                outline: none;
                font-size: 0.9rem;

                &:focus {
                    border-color: #3b82f6;
                }
            }

            &__dropzone {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 20px;
                border: 2px dashed #cbd5e1;
                border-radius: 8px;
                background: #fafafa;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover,
                &.is-dragging {
                    border-color: #3b82f6;
                    background: #eff6ff;
                }

                .dropzone-text {
                    margin-top: 8px;
                    font-size: 0.875rem;
                    color: #475569;
                }

                .browse-link {
                    color: #3b82f6;
                    font-weight: 600;
                    text-decoration: underline;
                }

                .dropzone-limits {
                    margin-top: 4px;
                    font-size: 0.75rem;
                    color: #94a3b8;
                }
            }

            &__actions {
                display: flex;
                justify-content: flex-end;
                gap: 10px;
                margin-top: 20px;
                padding-top: 16px;
                border-top: 1px dashed #e2e8f0;
            }
        }

        &__empty-state {
            padding: 32px 16px;
            text-align: center;
            color: #94a3b8;
            font-size: 0.9rem;
            background: #f8fafc;
            border-radius: 8px;
            border: 1px dashed #cbd5e1;
            margin-bottom: 20px;
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            margin-top: 16px;
        }
    }
</style>
